import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { MapPin, MoveRight, Tag } from 'lucide-react';
import type { StockPlaceDto, Unit } from '@eombor/contracts';
import { Badge, Button, Dialog, EmptyState, Skeleton, cn } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import { unitText } from '../../../shared/i18n/labels.js';
import { usePermission } from '../../auth/model/usePermission.js';
import { RackLocator } from './RackLocator.js';
import type { LayoutRack } from '../../warehouse/api/warehouse.api.js';
import {
  useWarehouseLayout,
  warehouseKeys,
} from '../../warehouse/model/warehouse.queries.js';
import { stockKeys, useStockSearch } from '../model/stock.queries.js';
import { MoveStockDialog, type MoveStockTarget } from './MoveStockDialog.js';
import { BatchLabelDialog, type BatchLabelTarget } from './BatchLabelDialog.js';

/** Miqdorni ekranga chiqarish — `numeric(14,3)` bilan bir xil yumaloqlash. */
const qtyText = (qty: number): string => String(Math.round(qty * 1000) / 1000);

const amountText = (qty: number, unit: Unit): string => `${qtyText(qty)} ${unitText(unit)}`;

export interface StockLocationDialogProps {
  open: boolean;
  onClose: () => void;
  warehouseId: string;
  nomenclatureId: string;
  /** Sarlavhada ko'rinadi — oyna qaysi mahsulot haqida ekanini aytadi. */
  nomenclatureName: string;
  /** Ko'chirishdan keyin chaqiriladi — chaqiruvchi o'z ro'yxatini yangilaydi. */
  onMoved?: () => void;
}

/**
 * «BU MAHSULOT QAYERDA TURIBDI» — va kerak bo'lsa, KO'CHIRISH.
 *
 * Nega kerak bo'lgani aniq bir holatdan kelib chiqadi. Ko'chirish tizimda
 * ALLAQACHON bor edi (`POST /stock/move`), lekin unga bitta yo'l bor edi:
 * Ombor → katak kartochkasi. Ya'ni skladchi ko'chirish uchun mahsulot
 * qaysi katakda ekanini OLDINDAN bilishi kerak edi — u esa aynan shuni
 * bilmaydi. Natijada imkoniyat bor, lekin undan hech kim foydalanmaydi.
 *
 * Shuning uchun oyna savolga javob berishdan BOSHLANADI: chizmada
 * kataklar yonib turadi, qolgani so'nadi. Ko'chirish esa javobning
 * yonida turadi — odam mahsulotni topgan joyida, boshqa ekranga
 * o'tmasdan tuzatadi.
 *
 * IKKI USTUN, ikkalasi ham bir narsani aytadi: chapda aniq sonlar
 * (qaysi partiya, qancha bor, qanchasi band), o'ngda chizma. Chapdagi
 * satr tanlansa chizmada o'sha katak, chizmadagi katak bosilsa chapda
 * o'sha satr belgilanadi — bitta tanlov, ikki ko'rinish.
 */
export const StockLocationDialog = ({
  open,
  onClose,
  warehouseId,
  nomenclatureId,
  nomenclatureName,
  onMoved,
}: StockLocationDialogProps) => {
  const queryClient = useQueryClient();
  const { can } = usePermission();

  const [selected, setSelected] = useState<string | null>(null);
  const [moving, setMoving] = useState<{ place: StockPlaceDto } | null>(null);
  const [labelling, setLabelling] = useState(false);

  /* So'rov faqat oyna OCHIQ bo'lganda: ro'yxatdagi har bir qator uchun
     oyna chizilgan bo'ladi va yopiq oynalar birdaniga o'ntalab so'rov
     yuborardi. */
  const stock = useStockSearch({ warehouseId, nomenclatureId, limit: 200 }, open);
  const layout = useWarehouseLayout(open ? warehouseId : null);

  const places = useMemo<StockPlaceDto[]>(
    () => (stock.data?.items ?? []).flatMap((group) => group.places),
    [stock.data],
  );

  /* Ochilganda birinchi joy TANLANADI: ko'pincha bitta katak bo'ladi va
     odam «avval tanlang» degan ko'rsatmani o'qib o'tirmasligi kerak. */
  useEffect(() => {
    if (!open) {
      setSelected(null);
      return;
    }
    setSelected((current) => {
      if (current !== null && places.some((place) => place.cellId === current)) return current;
      return places[0]?.cellId ?? null;
    });
  }, [open, places]);

  const racks = useMemo<LayoutRack[]>(
    () => (layout.data?.zones ?? []).flatMap((zone) => zone.racks),
    [layout.data],
  );

  /* Tanlangan katakning javoni ko'rsatiladi; tanlov bo'lmasa mahsulot
     turgan birinchi katakniki. */
  const facadeRack = useMemo<LayoutRack | null>(() => {
    if (racks.length === 0) return null;
    const wanted = selected ?? places[0]?.cellId ?? null;
    return racks.find((rack) => rack.cells.some((cell) => cell.id === wanted)) ?? null;
  }, [racks, selected, places]);

  /*
   * Chizmada belgilanadigan kataklar — FAQAT joriy javondagilar.
   *
   * `marked` bo'sh bo'lmasa `RackFacade` qolgan kataklarni so'ndiradi.
   * Boshqa javondagi kataklar ham ro'yxatga kirsa, ular hech qachon
   * chizilmaydi-yu, lekin xarita «bo'sh emas» bo'lib qolardi — ya'ni
   * so'nish sababsiz ko'rinardi.
   */
  const marked = useMemo<ReadonlyMap<string, string>>(() => {
    const map = new Map<string, string>();
    if (facadeRack === null) return map;
    for (const place of places) {
      if (!facadeRack.cells.some((cell) => cell.id === place.cellId)) continue;
      const before = map.get(place.cellId);
      const text = amountText(place.qty, place.unit);
      map.set(place.cellId, before === undefined ? text : `${before} · ${text}`);
    }
    return map;
  }, [places, facadeRack]);

  const warehouse = layout.data?.warehouse;
  const scopeRef = {
    warehouseId,
    ...(warehouse === undefined ? {} : { mtuId: warehouse.mtuId, orgUnitId: warehouse.orgUnitId }),
  };
  const canMove = can('stock.move', scopeRef);
  const canPrint = can('warehouse.label.print', scopeRef);

  /**
   * Etiketka bosiladigan PARTIYALAR — ko'rinib turgan joylashuvlardan.
   *
   * Bitta partiya bir necha katakda yotishi mumkin, etiketka esa
   * partiyaniki — shuning uchun ro'yxat `batchId` bo'yicha yagonalanadi.
   * Aks holda skladchi bir xil ikkita etiketka olardi va ularning farqini
   * qidirib yurardi.
   *
   * Katak TANLANGAN bo'lsa faqat o'shaniki: odam aniq bir javonning
   * yorlig'ini qayta bosmoqchi bo'ladi, butun ombornikini emas.
   */
  const labelTargets = useMemo<BatchLabelTarget[]>(() => {
    const source = selected === null ? places : places.filter((place) => place.cellId === selected);
    const byBatch = new Map<string, BatchLabelTarget>();
    for (const place of source) {
      if (byBatch.has(place.batchId)) continue;
      byBatch.set(place.batchId, {
        batchId: place.batchId,
        batchNo: place.batchNo,
        name: place.nomenclatureName,
        hint: `${place.cellCode} · ${amountText(place.qty, place.unit)}`,
      });
    }
    return [...byBatch.values()];
  }, [places, selected]);

  const selectedPlaces = places.filter((place) => place.cellId === selected);

  const body = (): React.ReactNode => {
    if (stock.isPending || layout.isPending) return <Skeleton lines={6} />;
    if (places.length === 0) {
      return (
        <EmptyState
          icon={<MapPin size={20} aria-hidden />}
          title={t('stock.locationEmpty')}
          description={t('stock.locationEmptyHint')}
        />
      );
    }

    return (
      /*
       * USTMA-UST, YONMA-YON EMAS.
       *
       * Avval ikki ustun edi: chapda ro'yxat, o'ngda chizma. Javon esa
       * o'n uchta o'rinli bo'lishi mumkin va qolgan yarim enga sig'may,
       * gorizontal siljish bilan chiqardi — chizmaning yarmi ko'rinmas
       * joyda qolardi.
       *
       * Endi ro'yxat tepada, yonma-yon kartochkalar bo'lib turadi
       * (ko'pincha ular bitta yoki ikkita) va chizmaga oynaning BUTUN
       * eni qoladi.
       */
      <div className="flex flex-col gap-4">
        {/* Telefonda bitta ustun, planshetda ikki, kengda esa qatorda —
            har kartochka ikki qator: «katak · miqdor» va «partiya · band». */}
        <ul
          aria-label={t('stock.locationCells')}
          className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:flex lg:flex-wrap"
        >
          {places.map((place) => {
            const active = place.cellId === selected;
            return (
              <li key={`${place.cellId}:${place.batchId}`} className="min-w-0">
                <button
                  type="button"
                  aria-pressed={active}
                  onClick={() => setSelected(place.cellId)}
                  className={cn(
                    'flex min-h-11 w-full flex-col justify-center gap-1 rounded-[var(--radius-md)] border px-3 py-2 text-left',
                    'transition-colors duration-[var(--motion-fast)]',
                    active
                      ? 'border-accent bg-accent-soft'
                      : 'border-border bg-surface hover:bg-surface-2',
                  )}
                >
                  <span className="flex items-baseline gap-3">
                    <span className="font-mono text-sm font-semibold text-text">
                      {place.cellCode}
                    </span>
                    <span className="ml-auto tabular-nums text-sm font-semibold text-text">
                      {amountText(place.qty, place.unit)}
                    </span>
                  </span>
                  <span className="flex min-w-0 items-center gap-2">
                    <span className="truncate font-mono text-xs text-text-muted">
                      {place.batchNo}
                    </span>
                    {/* BAND qilingani ALOHIDA aytiladi: uni ko'chirib
                        bo'lmaydi va sababini oyna ochilgandayoq bilish
                        kerak, «Ko'chirish» bosilgandan keyin emas. */}
                    {place.reservedQty > 0 ? (
                      <Badge tone="warn">
                        {t('stock.locationReserved', {
                          qty: amountText(place.reservedQty, place.unit),
                        })}
                      </Badge>
                    ) : null}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>

        <div className="min-w-0">
          {facadeRack === null ? (
            <p className="text-sm text-text-muted">{t('stock.locationNoRack')}</p>
          ) : (
            /*
             * JAVON TANLAGICHI ATAYLAB YO'Q (`racks` berilmaydi).
             *
             * Bu oynada javonni odam emas, TANLOV tanlaydi: tepadagi
             * ro'yxatdan katak bosilsa, chizma o'sha javonga o'zi
             * o'tadi. Tanlagich qo'yilsa, mahsulot yo'q javonga o'tish
             * mumkin bo'lardi — u yerda hamma katak so'nib, chizma
             * buzilgandek ko'rinardi.
             */
            <RackLocator
              rack={facadeRack}
              marked={marked}
              selectedCellId={selected}
              onSelectCell={(id) => {
                if (!marked.has(id)) return;
                setSelected(id);
              }}
            />
          )}
        </div>
      </div>
    );
  };

  /* Ko'chirish faqat BITTA partiyaga: «qaysi biri?» degan savolga javobni
     oyna emas, odam berishi kerak. Katakda bitta partiya bo'lsa savol
     ham tug'ilmaydi. */
  const movable = selectedPlaces.length === 1 ? selectedPlaces[0] : undefined;

  return (
    <>
      <Dialog
        open={open && moving === null && !labelling}
        onClose={onClose}
        size="xl"
        title={t('stock.locationTitle', { name: nomenclatureName })}
        description={t('stock.locationHint')}
        /* Telefonda varaqning tepasidagi TUTQICH — u pastdan chiqqanini
           ko'rsatadi. Umumiy `Dialog` ga tegilmaydi: chiziq panelning
           `::before` i, faqat shu oynada. */
        className={cn(
          "max-sm:before:absolute max-sm:before:top-2 max-sm:before:left-1/2 max-sm:before:z-10 max-sm:before:content-['']",
          'max-sm:before:h-1 max-sm:before:w-10 max-sm:before:-translate-x-1/2 max-sm:before:rounded-full',
          'max-sm:before:bg-border-strong',
        )}
        /* Tugmalar `footer` ga TO'G'RIDAN-TO'G'RI beriladi, o'ramsiz:
           `Dialog` ning telefon qoidalari (ustma-ust, to'liq en, 44px)
           har bir tugmaga tegishi uchun. Ilgari ular bitta `div` ichida
           edi va qoida faqat o'sha `div` ga tushib, uchta tugma tor
           ekranda bir qatorga siqilardi. */
        footer={
          <>
            <Button variant="ghost" onClick={onClose}>
              {t('stock.close')}
            </Button>
            {canPrint && labelTargets.length > 0 ? (
              <Button variant="ghost" onClick={() => setLabelling(true)}>
                <Tag size={15} aria-hidden />
                {t('stock.locationLabel')}
              </Button>
            ) : null}
            {canMove && movable !== undefined ? (
              <Button variant="secondary" onClick={() => setMoving({ place: movable })}>
                <MoveRight size={15} aria-hidden />
                {t('stock.locationMove', { cell: movable.cellCode })}
              </Button>
            ) : null}
          </>
        }
      >
        {body()}
      </Dialog>

      <BatchLabelDialog
        open={labelling}
        onClose={() => setLabelling(false)}
        targets={labelTargets}
      />

      <MoveStockDialog
        open={moving !== null}
        onClose={() => setMoving(null)}
        warehouseId={warehouseId}
        fromCellId={moving?.place.cellId ?? ''}
        fromCellCode={moving?.place.cellCode ?? ''}
        target={targetOf(moving?.place)}
        onMoved={() => {
          setMoving(null);
          /* Qoldiq ham, chizma ham SERVERDAN qayta o'qiladi: ko'chirishdan
             keyin nima qayerda turganini taxmin qilish emas, ko'rish
             kerak. */
          void queryClient.invalidateQueries({ queryKey: stockKeys.all });
          void queryClient.invalidateQueries({ queryKey: warehouseKeys.all });
          onMoved?.();
        }}
      />
    </>
  );
};

/** Joylashuvdan ko'chirish oynasi kutadigan shakl. */
const targetOf = (place: StockPlaceDto | undefined): MoveStockTarget | null =>
  place === undefined
    ? null
    : {
        batchId: place.batchId,
        batchNo: place.batchNo,
        nomenclatureId: place.nomenclatureId,
        nomenclatureName: place.nomenclatureName,
        unit: place.unit,
        qty: place.qty,
        availableQty: place.availableQty,
      };
